<template>
  <div class="ex-employee-checkpoint-learn-more-card">
    <div class="ex-employee-checkpoint-learn-more-card-header-actions">
      <div class="ex-employee-checkpoint-learn-more-card-breadcrumbs">
        <ExBreadcrumbs
          :breadcrumbsList="breadcrumbItems"
          @onClickBreadcrumb="handleBreadcrumbClick"
        />
      </div>
      <div class="ex-employee-checkpoint-learn-more-card-level">
        <CheckpointLevelBand :level="props.readiness_level" />
      </div>
      <div class="ex-employee-checkpoint-learn-more-card-watch-video">
        <WatchVideoButton
          v-if="props.video_url"
          class="watch-video"
          label="Watch Video"
          @onClick="handleWatchVideo"
        />
      </div>
    </div>
    <div
      class="ex-employee-checkpoint-learn-more-card-criteria-content-container"
    >
      <AsEmployeeCheckpointLearnMoreCriteriaBar
        :checkpointStatus="props.status"
        :inspectedBy="props.inspector_info.inspected_by"
        :inspectedByAvatar="props.inspector_info.inspected_by_avatar"
        :inspectedOn="props.inspector_info.inspected_on"
        :reason="props.inspector_info.reason"
        :criteria="props.criteria"
        :prefix="props.prefix"
      />
    </div>
    <div
      v-if="hasInstructions && !props.set_to_microlearning_instructions"
      class="ex-employee-checkpoint-learn-more-card-criteria-content-instructions"
      v-html="props.instructions"
    ></div>
    <div v-if="props.set_to_microlearning_instructions">
      <AsEmployeeVideoCheckpointInstructions
        :video="microlearningVideo"
        :error="microlearningError"
        :summary="microlearningSummary"
        :steps="microlearningSteps"
        :selectedLanguage="microlearningSelectedLanguage"
        :languages="microlearningLanguages"
        @update:selectedLanguage="handleLanguageUpdate"
        @language-selected="handleLanguageSelected"
      />
    </div>

    <v-dialog
      @click:outside="closeVideoDialog"
      v-model="showVideoPlayer"
      max-width="680px"
      class="video-dialog"
    >
      <v-card class="video-card">
        <v-card-title class="video-title">
          <span class="title-text">Watch Video</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeVideoDialog" class="close-btn">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="video-content p-3">
          <div
            v-if="videoContent"
            v-html="videoContent"
            class="video-iframe-container"
          ></div>
          <div v-else class="no-content">
            <v-icon size="64" color="grey">mdi-video-off</v-icon>
            <p>No video content available</p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import CheckpointLevelBand from "@components/CheckpointLevelBand/CheckpointLevelBand.vue";
import WatchVideoButton from "@components/WatchVideoButton/WatchVideoButton.vue";
import CheckpointInspectionStatusBadge from "@components/CheckpointInspectionStatusBadge/CheckpointInspectionStatusBadge.vue";
import AsEmployeeCheckpointLearnMoreCriteriaBar from "@components/AsEmployeeCheckpointLearnMoreCriteriaBar/AsEmployeeCheckpointLearnMoreCriteriaBar.vue";
import AsEmployeeVideoCheckpointInstructions from "@components/AsEmployeeVideoCheckpointInstructions/AsEmployeeVideoCheckpointInstructions.vue";
const emit = defineEmits(["back-to-checklist"]);

const showVideoPlayer = ref(false);
const videoContent = ref(null);

const hasInstructions = computed(() => {
  if (!props.instructions) return false;
  // Strip HTML tags and check if there's actual content
  const strippedContent = props.instructions.replace(/<[^>]*>/g, "").trim();
  return strippedContent.length > 0;
});

// Computed properties for microlearning instructions
const microlearningVideo = computed(() => {
  const instructions = props.microlearning_instructions || {};
  return instructions.video_url || instructions.video || props.video_url || '';
});

const microlearningError = computed(() => {
  const instructions = props.microlearning_instructions || {};
  return instructions.error || null;
});

const microlearningSummary = computed(() => {
  const instructions = props.microlearning_instructions || {};
  return instructions.summary || null;
});

const microlearningSteps = computed(() => {
  const instructions = props.microlearning_instructions || {};
  return instructions.steps || [];
});

const microlearningSelectedLanguage = computed(() => {
  const instructions = props.microlearning_instructions || {};
  return instructions.selectedLanguage || instructions.selected_language || null;
});

const microlearningLanguages = computed(() => {
  const instructions = props.microlearning_instructions || {};
  return instructions.languages || [];
});

const props = defineProps({
  prefix: {
    type: String,
    default: null,
  },
  video_url: {
    type: String,
    default: "",
  },
  status: {
    type: String,
    default: "",
    validator: (value) => {
      return [
        "Not yet evaluated",
        "Reset",
        "Passed",
        "Failed",
        "NA",
        "",
      ].includes(value);
    },
  },
  responsibility: {
    type: String,
    default: "",
  },
  role: {
    type: String,
    default: "",
  },
  position: {
    type: String,
    default: "",
  },
  available_inspectors: {
    type: Array,
    default: [],
  },
  inspection_status: {
    type: Array,
    default: () => [],
  },
  readiness_level: {
    type: String,
    default: "Apprentice",
    validator: (value) => {
      return ["Apprentice", "Professional", "Coach", "Master"].includes(value);
    },
  },
  criteria: {
    type: String,
    default: "",
  },
  instructions: {
    type: String,
    default: "",
  },
  inspector_info: {
    type: Object,
    default: () => ({
      inspected_by: "",
      inspected_by_avatar: "",
      inspected_on: "",
      reason: "",
      status: "",
    }),
  },
  set_to_microlearning_instructions: {
    type: Boolean,
    default: false,
  },
  microlearning_instructions: {
    type: Object,
    default: () => ({}),
  },
});
const breadcrumbItems = [
  {
    text: "Checklist",
    href: null, // Will be handled by click event
  },
  {
    text: "Checkpoint",
    disabled: true,
  },
];

const handleBreadcrumbClick = (breadcrumb) => {
  if (breadcrumb.text === "Checklist") {
    emit("back-to-checklist");
  }
};

const handleWatchVideo = () => {
  if (!props.video_url) return;

  const isProcessing =
    props.video_url &&
    (props.video_url.includes("being processed") ||
      props.video_url.includes("will be available in a moment") ||
      props.video_url.includes("processing"));

  if (isProcessing) {
    return;
  }

  videoContent.value = props.video_url;
  showVideoPlayer.value = true;
};

const closeVideoDialog = () => {
  showVideoPlayer.value = false;
  videoContent.value = null;
};

const handleLanguageUpdate = (language) => {
  // Handle language update if needed
  // You can emit an event or update local state here
};

const handleLanguageSelected = (language) => {
  // Handle language selection if needed
  // You can emit an event or update local state here
};
</script>

<style scoped>
.ex-employee-checkpoint-learn-more-card {
  width: 100%;
}

.ex-employee-checkpoint-learn-more-card-header-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ex-employee-checkpoint-learn-more-card-watch-video {
  padding: 4px 0px;
}

.watch-video {
  padding: 0 !important;
}

.watch-video:hover {
  margin: 0 !important;
}

.ex-employee-checkpoint-learn-more-card-header-criteria-title {
  color: #000;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: 20px; /* 142.857% */
  padding: 4px 0px;
}

.ex-employee-checkpoint-learn-more-card-header {
  display: flex;
  justify-content: space-between;
}

.ex-employee-checkpoint-learn-more-card-header-status {
  padding: 0px 22px;
}

.ex-employee-checkpoint-learn-more-card-criteria-content {
  color: #000;
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}

.ex-employee-checkpoint-learn-more-card-criteria-content-container {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.ex-employee-checkpoint-learn-more-card-criteria-content-prefix {
  font-family: Inter;
  font-weight: 600;
  font-size: 14px;
  color: #666666;
  text-transform: capitalize;
}

.ex-employee-checkpoint-learn-more-card-criteria-content-prefix-container {
  display: flex;
  flex-direction: row;
  gap: 8px;
}

.video-dialog {
  border-radius: 12px;
}

.video-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
}

.video-title {
  background-color: #f9fafb;
  padding: 16px 24px;
  border-bottom: 1px solid #eaecf0;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.video-title .title-text {
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  color: #344054;
}

.video-title .close-btn {
  color: #667085;
}

.video-title .close-btn:hover {
  background-color: #f2f4f7;
}

.video-content {
  padding: 24px;
  background-color: #ffffff;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.video-iframe-container {
  width: 100%;
  height: 400px;
  margin: 0 auto;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.video-iframe-container :deep(iframe) {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
}

.no-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #667085;
  font-family: Inter;
  font-size: 16px;
  gap: 16px;
}

.no-content p {
  margin: 0;
  font-weight: 500;
}
:deep(.criteria-bar-container) {
  padding: 16px 16px 16px 0px;
}
</style>
